import { useState, useEffect, useRef, useCallback } from "react";
import { createChart, CandlestickSeries, ColorType, createSeriesMarkers } from "lightweight-charts";
import { Bot, Zap, Activity, LineChart, History } from "lucide-react";
import api from "@/lib/api";
import { money } from "@/lib/format";
import PriceChart from "@/components/market/PriceChart";

const digitsFor = (p) => (p && p > 10 ? 2 : 4);
const pipFor = (p) => (p && p > 10 ? 0.01 : 0.0001);
const SEED = 90;
const TIMEFRAMES = { M1: { step: 60, ms: 4000 }, M5: { step: 300, ms: 7000 }, M15: { step: 900, ms: 10000 } };

function seedCandles(price, stepSec) {
  const step = pipFor(price) * 6;
  const out = [];
  let t = Math.floor(Date.now() / 1000) - SEED * stepSec;
  let c = price;
  for (let i = 0; i < SEED; i++) {
    const o = c;
    c = Math.max(step, o + (Math.random() - 0.5) * step * 2.2);
    out.push({ time: t, open: o, high: Math.max(o, c) + Math.random() * step, low: Math.min(o, c) - Math.random() * step, close: c });
    t += stepSec;
  }
  return out;
}

export default function TradeTerminal({ onTradeClosed }) {
  const [pairs, setPairs] = useState([]);
  const [selected, setSelected] = useState("EUR/USD");
  const [prices, setPrices] = useState({});
  const [changes, setChanges] = useState({});
  const [positions, setPositions] = useState([]);
  const [tab, setTab] = useState("trade");
  const [timeframe, setTimeframe] = useState("M1");
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState({ total_pnl: 0, wins: 0, losses: 0, total: 0, win_rate: 0 });
  const [equity, setEquity] = useState([0]);

  const pricesRef = useRef({});
  const selRef = useRef(selected);
  const posRef = useRef([]);
  const holdRef = useRef({});
  const onClosedRef = useRef(onTradeClosed);
  const chartWrap = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const candlesRef = useRef([]);
  const markersRef = useRef(null);
  const markersArrRef = useRef([]);
  const candleTimeRef = useRef(0);
  const candleWallRef = useRef(0);
  const tfRef = useRef(timeframe);
  selRef.current = selected;
  tfRef.current = timeframe;
  posRef.current = positions;
  onClosedRef.current = onTradeClosed;

  const price = prices[selected];
  const digits = digitsFor(price);

  const loadTrades = useCallback(() => {
    api.get("/me/trades").then(({ data }) => {
      setHistory(data.trades || []);
      setSummary(data.summary || {});
      const asc = [...(data.trades || [])].reverse();
      let run = 0;
      setEquity([0, ...asc.map((t) => (run += t.pnl))]);
    }).catch(() => {});
  }, []);

  // market data seed + periodic re-anchor
  useEffect(() => {
    const load = () => api.get("/market/forex").then(({ data }) => {
      setPairs(data.map((d) => d.pair));
      const pm = {}, cm = {};
      data.forEach((d) => { pm[d.pair] = d.price; cm[d.pair] = d.change_24h; });
      setChanges(cm);
      setPrices((prev) => { const m = { ...pm, ...prev }; pricesRef.current = m; return m; });
    }).catch(() => {});
    load();
    const iv = setInterval(load, 12000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => { loadTrades(); const iv = setInterval(loadTrades, 9000); return () => clearInterval(iv); }, [loadTrades]);

  // load persisted open positions
  useEffect(() => {
    api.get("/me/positions").then(({ data }) => {
      const now = Date.now();
      const loaded = data.map((p) => ({ id: p.id, pair: p.pair, side: p.side, entry: p.entry, volume: p.volume, digits: p.digits, opened_at: p.opened_at, goal: +(Math.random() * 1.6 + 0.05).toFixed(2), openTs: now }));
      loaded.forEach((p) => { holdRef.current[p.id] = now + 6000 + Math.random() * 12000; });
      setPositions(loaded);
    }).catch(() => {});
  }, []);

  // create candlestick chart once
  useEffect(() => {
    if (!chartWrap.current) return;
    const chart = createChart(chartWrap.current, {
      autoSize: true,
      localization: { locale: "en-US" },
      layout: { background: { type: ColorType.Solid, color: "#0a0b0f" }, textColor: "#8b93a7", fontFamily: "ui-monospace, monospace" },
      grid: { vertLines: { color: "rgba(148,163,184,0.05)" }, horzLines: { color: "rgba(148,163,184,0.05)" } },
      timeScale: { timeVisible: true, secondsVisible: false, borderColor: "rgba(212,175,55,0.2)" },
      rightPriceScale: { borderColor: "rgba(212,175,55,0.2)" },
      crosshair: { mode: 0, vertLine: { color: "rgba(212,175,55,0.4)", labelBackgroundColor: "#d4af37" }, horzLine: { color: "rgba(212,175,55,0.4)", labelBackgroundColor: "#d4af37" } },
    });
    const series = chart.addSeries(CandlestickSeries, {
      upColor: "#22c55e", downColor: "#ef4444", wickUpColor: "#22c55e", wickDownColor: "#ef4444", borderVisible: false,
    });
    chartRef.current = chart;
    seriesRef.current = series;
    markersRef.current = createSeriesMarkers(series, []);
    return () => { chart.remove(); chartRef.current = null; seriesRef.current = null; markersRef.current = null; markersArrRef.current = []; };
  }, []);

  // reseed candles when symbol changes
  useEffect(() => {
    candlesRef.current = [];
    markersArrRef.current = [];
    markersRef.current?.setMarkers([]);
    const d = digitsFor(pricesRef.current[selected]);
    seriesRef.current?.applyOptions({ priceFormat: { type: "price", precision: d, minMove: d === 2 ? 0.01 : 0.0001 } });
  }, [selected, timeframe]);

  // live tick: walk all pairs + drive the candle for the selected symbol
  useEffect(() => {
    const iv = setInterval(() => {
      const cur = pricesRef.current;
      if (!cur || !Object.keys(cur).length) return;
      const next = {};
      for (const [pair, p] of Object.entries(cur)) {
        const step = pipFor(p);
        next[pair] = Math.max(step, p + (Math.random() - 0.5) * step * 3);
      }
      pricesRef.current = next;
      setPrices(next);
      const sp = next[selRef.current];
      if (sp == null || !seriesRef.current) return;
      const tf = TIMEFRAMES[tfRef.current] || TIMEFRAMES.M1;
      if (!candlesRef.current.length) {
        candlesRef.current = seedCandles(sp, tf.step);
        candleTimeRef.current = candlesRef.current[candlesRef.current.length - 1].time;
        candleWallRef.current = Date.now();
        seriesRef.current.setData(candlesRef.current);
        return;
      }
      const arr = candlesRef.current;
      const c = arr[arr.length - 1];
      if (Date.now() - candleWallRef.current >= tf.ms) {
        candleTimeRef.current += tf.step;
        const nc = { time: candleTimeRef.current, open: c.close, high: sp, low: sp, close: sp };
        arr.push(nc);
        if (arr.length > 240) arr.shift();
        candleWallRef.current = Date.now();
        seriesRef.current.update(nc);
      } else {
        c.high = Math.max(c.high, sp); c.low = Math.min(c.low, sp); c.close = sp;
        seriesRef.current.update(c);
      }
    }, 1000);
    return () => clearInterval(iv);
  }, []);

  // unrealized P&L: small mostly-positive drift toward a tiny per-position goal
  const pnlOf = (pos) => {
    const closeAt = holdRef.current[pos.id] || (pos.openTs || Date.now()) + 12000;
    const openTs = pos.openTs || Date.now();
    const dur = Math.max(3000, closeAt - openTs);
    const prog = Math.min(1, (Date.now() - openTs) / dur);
    const goal = pos.goal ?? 0.2;
    return Math.max(-goal * 0.2, goal * (prog - 0.1) + Math.sin(openTs / 900 + Date.now() / 1600) * goal * 0.05);
  };

  // place buy/sell + close arrows on the candlestick chart at the current bar
  const addMarker = useCallback((side, kind) => {
    if (!markersRef.current || !candleTimeRef.current) return;
    const m = kind === "open"
      ? { time: candleTimeRef.current, position: side === "BUY" ? "belowBar" : "aboveBar", color: side === "BUY" ? "#22c55e" : "#ef4444", shape: side === "BUY" ? "arrowUp" : "arrowDown", text: side }
      : { time: candleTimeRef.current, position: "inBar", color: "#eab308", shape: "circle", text: "×" };
    const arr = [...markersArrRef.current, m].slice(-60).sort((a, b) => a.time - b.time);
    markersArrRef.current = arr;
    markersRef.current.setMarkers(arr);
  }, []);

  // AUTO-TRADING engine — opens & closes on its own
  useEffect(() => {
    const doClose = async (pos) => {
      const cur = pricesRef.current[pos.pair] ?? pos.entry;
      setPositions((prev) => prev.filter((x) => x.id !== pos.id));
      delete holdRef.current[pos.id];
      try {
        await api.post("/me/trades/close", { pair: pos.pair, side: pos.side, volume: pos.volume, entry: +pos.entry.toFixed(pos.digits), exit: +cur.toFixed(pos.digits), opened_at: pos.opened_at });
        await api.delete(`/me/positions/${pos.id}`).catch(() => {});
        addMarker(pos.side, "close");
        loadTrades();
        onClosedRef.current && onClosedRef.current();
      } catch { /* markets closed */ }
    };
    const doOpen = async () => {
      const cur = pricesRef.current;
      const list = Object.keys(cur);
      if (!list.length) return;
      const pair = list[Math.floor(Math.random() * list.length)];
      const p = cur[pair];
      const d = digitsFor(p), pp = pipFor(p);
      const side = Math.random() > 0.5 ? "BUY" : "SELL";
      const vol = +(Math.random() * 1.4 + 0.1).toFixed(2);
      const entry = side === "BUY" ? p + pp : p - pp;
      const payload = { pair, side, entry: +entry.toFixed(d), volume: vol, digits: d, opened_at: new Date().toISOString() };
      try {
        const { data } = await api.post("/me/positions", payload);
        holdRef.current[data.id] = Date.now() + 9000 + Math.random() * 15000;
        const goal = +(Math.random() * 1.6 + 0.05).toFixed(2);
        setPositions((prev) => [{ id: data.id, ...payload, goal, openTs: Date.now() }, ...prev]);
        addMarker(side, "open");
      } catch { /* ignore */ }
    };
    const iv = setInterval(() => {
      const now = Date.now();
      posRef.current.forEach((pos) => { if (holdRef.current[pos.id] && now >= holdRef.current[pos.id]) doClose(pos); });
      if (posRef.current.length < 3 && Math.random() > 0.3) doOpen();
    }, 12000);
    return () => clearInterval(iv);
  }, [loadTrades, addMarker]);

  const change = changes[selected] || 0;
  const floating = positions.reduce((s, p) => s + pnlOf(p), 0);
  const balance = summary.total_pnl || 0;
  const equityVal = balance + floating;

  const fmtTime = (iso) => { try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }); } catch { try { return new Date(iso).toISOString().slice(5, 16).replace("T", " "); } catch { return "—"; } } };

  return (
    <div className="rounded-2xl border border-amber-500/20 bg-[#0a0b0f] overflow-hidden" data-testid="trade-terminal">
      {/* title bar */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-[#14151c] to-[#0a0b0f] border-b border-amber-500/15">
        <div className="flex items-center gap-2">
          <div className="relative"><Bot className="w-5 h-5 text-amber-400" /><span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /></div>
          <span className="font-display font-bold text-sm">MoonX Terminal</span>
          <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400 font-bold flex items-center gap-1"><Zap className="w-3 h-3" /> AUTO · LIVE</span>
        </div>
        <span className="text-[11px] text-slate-500 hidden sm:block">Trades execute automatically — no action needed</span>
      </div>

      {/* market watch */}
      <div className="flex gap-2 overflow-x-auto px-3 py-2 bg-[#0d0e13] border-b border-amber-500/10" data-testid="market-watch">
        {pairs.map((p) => {
          const up = (changes[p] || 0) >= 0;
          const act = p === selected;
          return (
            <button key={p} data-testid={`mw-${p}`} onClick={() => setSelected(p)}
              className={`shrink-0 px-3 py-1.5 rounded-lg border text-left transition-all ${act ? "border-amber-500/60 bg-amber-500/10" : "border-slate-800 hover:border-amber-500/30"}`}>
              <div className="text-[11px] font-semibold text-slate-200">{p}</div>
              <div className={`font-mono-fin text-xs ${up ? "text-emerald-400" : "text-red-400"}`}>{prices[p] != null ? prices[p].toFixed(digitsFor(prices[p])) : "--"}</div>
            </button>
          );
        })}
      </div>

      {/* chart */}
      <div className="flex items-center justify-end gap-1 px-3 py-1.5 bg-[#0d0e13] border-b border-amber-500/10" data-testid="timeframe-bar">
        <span className="text-[10px] text-slate-500 mr-1 uppercase tracking-wide">Timeframe</span>
        {Object.keys(TIMEFRAMES).map((tf) => (
          <button key={tf} data-testid={`tf-${tf}`} onClick={() => setTimeframe(tf)}
            className={`text-[11px] px-2.5 py-1 rounded font-bold transition-all ${timeframe === tf ? "bg-amber-500/20 text-amber-300 border border-amber-500/40" : "text-slate-500 hover:text-slate-300 border border-transparent"}`}>{tf}</button>
        ))}
      </div>
      <div className="relative">
        <div className="absolute top-2 left-3 z-10 flex items-center gap-2 pointer-events-none">
          <span className="text-sm font-bold text-slate-100">{selected}</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">{timeframe}</span>
          <span className={`font-mono-fin text-sm ${change >= 0 ? "text-emerald-400" : "text-red-400"}`}>{price != null ? price.toFixed(digits) : "--"} <span className="text-[10px]">({change >= 0 ? "+" : ""}{Number(change).toFixed(2)}%)</span></span>
        </div>
        <div ref={chartWrap} className="w-full h-[300px]" data-testid="candle-chart" />
      </div>

      {/* terminal dock */}
      <div className="border-t border-amber-500/20 bg-[#0d0e13]">
        <div className="flex items-center gap-1 px-2 pt-2">
          {[{ k: "trade", l: "Trade", i: Activity }, { k: "history", l: "Account History", i: History }, { k: "equity", l: "Equity", i: LineChart }].map((t) => (
            <button key={t.k} data-testid={`term-tab-${t.k}`} onClick={() => setTab(t.k)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-t-lg text-xs font-semibold transition-all ${tab === t.k ? "bg-[#14151c] text-amber-300 border border-b-0 border-amber-500/25" : "text-slate-500 hover:text-slate-300"}`}>
              <t.i className="w-3.5 h-3.5" /> {t.l}
            </button>
          ))}
        </div>

        {/* account summary strip */}
        <div className="flex flex-wrap gap-x-6 gap-y-1 px-4 py-2 bg-[#14151c] border-y border-amber-500/10 text-xs font-mono-fin" data-testid="account-summary">
          <span className="text-slate-400">Balance: <b className="text-slate-100">{money(balance)}</b></span>
          <span className="text-slate-400">Equity: <b className="text-amber-300">{money(equityVal)}</b></span>
          <span className="text-slate-400">Floating: <b className={floating >= 0 ? "text-emerald-400" : "text-red-400"}>{floating >= 0 ? "+" : ""}{money(floating)}</b></span>
          <span className="text-slate-400">Open: <b className="text-slate-100">{positions.length}</b></span>
          <span className="text-slate-400 hidden sm:inline">Win rate: <b className="text-emerald-400">{summary.win_rate || 0}%</b></span>
        </div>

        <div className="p-3 min-h-[190px]">
          {tab === "trade" && (
            positions.length === 0 ? <p className="text-slate-600 text-xs py-6 text-center">No open positions — the engine will open the next trade shortly.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs min-w-[640px]" data-testid="positions-table">
                  <thead><tr className="text-amber-400/80 uppercase text-[10px] tracking-wider border-b border-amber-500/15">
                    <th className="text-left py-2">Symbol</th><th className="text-left py-2">Type</th><th className="text-right py-2">Volume</th><th className="text-right py-2">Open</th><th className="text-right py-2">Current</th><th className="text-right py-2">S/L</th><th className="text-right py-2">T/P</th><th className="text-right py-2">Profit</th>
                  </tr></thead>
                  <tbody>
                    {positions.map((pos) => {
                      const p = pnlOf(pos); const c = prices[pos.pair];
                      return (
                        <tr key={pos.id} className="border-b border-slate-800/50" data-testid={`pos-row-${pos.id}`}>
                          <td className="py-2 font-medium text-slate-200">{pos.pair}</td>
                          <td className={`py-2 font-semibold ${pos.side === "BUY" ? "text-emerald-400" : "text-red-400"}`}>{pos.side.toLowerCase()}</td>
                          <td className="py-2 text-right font-mono-fin text-slate-300">{pos.volume.toFixed(2)}</td>
                          <td className="py-2 text-right font-mono-fin text-slate-400">{pos.entry.toFixed(pos.digits)}</td>
                          <td className="py-2 text-right font-mono-fin text-slate-300">{c != null ? c.toFixed(pos.digits) : "--"}</td>
                          <td className="py-2 text-right font-mono-fin text-slate-600">0.00</td>
                          <td className="py-2 text-right font-mono-fin text-slate-600">0.00</td>
                          <td className={`py-2 text-right font-mono-fin font-bold ${p >= 0 ? "text-emerald-400" : "text-red-400"}`} data-testid={`pos-pnl-${pos.id}`}>{p >= 0 ? "+" : ""}{p.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          )}

          {tab === "history" && (
            history.length === 0 ? <p className="text-slate-600 text-xs py-6 text-center">No closed trades yet — history builds as the engine trades.</p> : (
              <div className="overflow-x-auto max-h-[320px] overflow-y-auto">
                <table className="w-full text-xs min-w-[640px]" data-testid="history-table">
                  <thead><tr className="text-amber-400/80 uppercase text-[10px] tracking-wider border-b border-amber-500/15 sticky top-0 bg-[#0d0e13]">
                    <th className="text-left py-2">Time</th><th className="text-left py-2">Symbol</th><th className="text-left py-2">Type</th><th className="text-right py-2">Volume</th><th className="text-right py-2">Open</th><th className="text-right py-2">Close</th><th className="text-right py-2">Profit</th>
                  </tr></thead>
                  <tbody>
                    {history.map((t) => (
                      <tr key={t.id} className="border-b border-slate-800/40" data-testid={`hist-row-${t.id}`}>
                        <td className="py-2 text-slate-500">{fmtTime(t.closed_at)}</td>
                        <td className="py-2 font-medium text-slate-200">{t.pair}</td>
                        <td className={`py-2 font-semibold ${t.side === "BUY" ? "text-emerald-400" : "text-red-400"}`}>{t.side.toLowerCase()}</td>
                        <td className="py-2 text-right font-mono-fin text-slate-300">{Number(t.volume).toFixed(2)}</td>
                        <td className="py-2 text-right font-mono-fin text-slate-400">{t.entry}</td>
                        <td className="py-2 text-right font-mono-fin text-slate-400">{t.exit}</td>
                        <td className={`py-2 text-right font-mono-fin font-bold ${t.pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>{t.pnl >= 0 ? "+" : ""}{money(t.pnl)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {tab === "equity" && (
            <div data-testid="equity-panel">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-slate-400 uppercase tracking-wide">Account Balance / Equity</span>
                <span className={`font-mono-fin text-sm font-bold ${balance >= 0 ? "text-emerald-400" : "text-red-400"}`}>{balance >= 0 ? "+" : ""}{money(balance)}</span>
              </div>
              {equity.length >= 2 ? (
                <div className="rounded-lg bg-black/30 border border-amber-500/10 p-2"><PriceChart data={equity} up={balance >= 0} height={200} /></div>
              ) : (
                <div className="h-[200px] grid place-items-center text-slate-600 text-xs">Equity curve builds as trades close.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
